/**
 * GPS Map View for Web (Google Maps)
 * Adapted from existing mymap.web.js for GPS tracking
 */

import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { useTheme } from 'react-native-paper';
import { memo, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, Circle, Polyline, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { LoadingScreen } from '@/src/components/LoadingScreen';
import type { LivePosition } from '../../types/gps';

// Google Maps API Key (reusing existing key)
const GOOGLE_MAPS_API_KEY = 'AIzaSyCdFGHSm2PGHB5ZNDC6uPxzYGHACCwlcbQ';
// Map ID required for Advanced Markers
// Can be overridden via EXPO_PUBLIC_GOOGLE_MAPS_MAP_ID environment variable
const GOOGLE_MAPS_MAP_ID = process.env.EXPO_PUBLIC_GOOGLE_MAPS_MAP_ID || 'a1949eda607095f8eef5f713';
// Use Advanced Markers when Map ID is available
const USE_ADVANCED_MARKERS = !!GOOGLE_MAPS_MAP_ID;

interface GPSMapViewProps {
  style?: ViewStyle;
  region?: {
    latitude: number;
    longitude: number;
    latitudeDelta?: number;
    longitudeDelta?: number;
  };
  showsUserLocation?: boolean;
  positions?: LivePosition[];
  onMarkerPress?: (position: LivePosition) => void;
  selectedDeviceId?: number;
  // Route visualization
  routePositions?: Array<{
    latitude: number;
    longitude: number;
    speed?: number;
    fixTime?: string;
  }>;
  alertMarkers?: Array<{
    latitude: number;
    longitude: number;
    title?: string;
    description?: string;
    color?: string;
    speed?: number;
    speedLimit?: number;
    eventType?: string;
    deviceName?: string;
    address?: string;
    attributes?: Record<string, any>;
  }>;
  showRoute?: boolean;
}

// Track if Google Maps script is loaded or loading
let googleMapsLoaded = false;
let googleMapsLoading = false;
const loadCallbacks: (() => void)[] = [];

// Load Google Maps script (singleton pattern)
const loadGoogleMapsScript = (callback: () => void) => {
  // If already loaded, verify Map constructor is available before calling callback
  if (
    googleMapsLoaded && 
    typeof window !== 'undefined' && 
    (window as any).google && 
    (window as any).google.maps &&
    typeof (window as any).google.maps.Map === 'function'
  ) {
    callback();
    return;
  }

  // Add callback to queue
  loadCallbacks.push(callback);

  // If already loading, just wait for the existing script to load
  if (googleMapsLoading) {
    return;
  }

  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    console.error('[GPS Map] Not in browser environment');
    return;
  }

  // Check if script already exists in DOM
  const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
  if (existingScript) {
    googleMapsLoading = true;
    // Check if Google Maps is already available
    const checkInterval = setInterval(() => {
      if (typeof window !== 'undefined' && (window as any).google && (window as any).google.maps) {
        clearInterval(checkInterval);
        googleMapsLoaded = true;
        googleMapsLoading = false;
        loadCallbacks.forEach(cb => cb());
        loadCallbacks.length = 0;
      }
    }, 100) as any;
    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!googleMapsLoaded) {
        console.error('[GPS Map] Timeout waiting for Google Maps to load');
        googleMapsLoading = false;
      }
    }, 10000);
    return;
  }

  // Start loading
  googleMapsLoading = true;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,drawing,marker&loading=async`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    // Wait for Google Maps API to be fully initialized
    // The script.onload fires when script tag loads, but API might not be ready yet
    const checkGoogleMapsReady = () => {
      if (
        typeof window !== 'undefined' &&
        (window as any).google &&
        (window as any).google.maps &&
        typeof (window as any).google.maps.Map === 'function'
      ) {
        // Google Maps is fully ready
        googleMapsLoaded = true;
        googleMapsLoading = false;
        // Call all queued callbacks
        loadCallbacks.forEach(cb => {
          try {
            cb();
          } catch (error) {
            console.error('[GPS Map] Error in callback:', error);
          }
        });
        loadCallbacks.length = 0;
      } else {
        // Not ready yet, check again in 50ms
        setTimeout(checkGoogleMapsReady, 50);
      }
    };
    
    // Start checking immediately
    checkGoogleMapsReady();
    
    // Timeout after 10 seconds
    setTimeout(() => {
      if (!googleMapsLoaded) {
        console.error('[GPS Map] Timeout waiting for Google Maps API to initialize');
        googleMapsLoading = false;
        loadCallbacks.length = 0;
      }
    }, 10000);
  };
  script.onerror = (error) => {
    googleMapsLoading = false;
    console.error('[GPS Map] Failed to load Google Maps script:', error);
    // Clear callbacks on error
    loadCallbacks.length = 0;
  };
  document.head.appendChild(script);
};

// Import the shared icon creation function
import { createGPSMarkerIcon } from './vehicleIconUtils';

const GPSMapView: React.FC<GPSMapViewProps> = ({ 
  style, 
  region, 
  showsUserLocation, 
  positions = [], 
  onMarkerPress, 
  selectedDeviceId,
  routePositions = [],
  alertMarkers = [],
  showRoute = false,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRefs = useRef<any[]>([]);
  const markerInfoWindowsRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const userLocationMarkerRef = useRef<any>(null);
  const polylineRef = useRef<any[]>([]);
  const [routeMaxSpeed, setRouteMaxSpeed] = useState<number>(0);
  const routeMarkersRef = useRef<any[]>([]);
  const alertMarkersRef = useRef<any[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const initTimeoutRef = useRef<any>(null);

  // Initialize map once
  useEffect(() => {
    if (!mapRef.current || initializedRef.current) return;
    
    // Set a timeout to detect if map doesn't load
    initTimeoutRef.current = setTimeout(() => {
      if (!isMapReady && !initializedRef.current) {
        console.error('[GPS Map] Map initialization timeout after 15 seconds');
        setMapError('Failed to load map. Please refresh the page.');
      }
    }, 15000);
    
    loadGoogleMapsScript(() => {
      if (!mapRef.current || initializedRef.current) return;
      
      try {
        // Initialize map with default region (Lisbon, Portugal)
        const initialCenter = {
          lat: region?.latitude || 38.7223,
          lng: region?.longitude || -9.1393
        };
        
        if (!(window as any).google || !(window as any).google.maps) {
          throw new Error('Google Maps API not available');
        }
        
        // Verify Map constructor is available
        if (typeof (window as any).google.maps.Map !== 'function') {
          throw new Error('Google Maps Map constructor not available yet');
        }
        
        mapInstanceRef.current = new (window as any).google.maps.Map(mapRef.current, {
          center: initialCenter,
          zoom: region ? 13 : 10, // Zoom out more if using default location
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          mapId: GOOGLE_MAPS_MAP_ID, // Required for Advanced Markers
        });

        // Initialize info window
        infoWindowRef.current = new (window as any).google.maps.InfoWindow();
        
        initializedRef.current = true;
        setIsMapReady(true);
        
        // Clear timeout since map loaded successfully
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
        }
      } catch (error) {
        console.error('[GPS Map] Failed to initialize map:', error);
        setMapError('Failed to initialize map. Please check console for details.');
      }
    });

    // Cleanup on unmount
    return () => {
      // Clear timeout
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      
      // Clear markers
      markerRefs.current.forEach(marker => {
        marker.setMap(null);
      });
      markerRefs.current = [];
      
      // Clear user location marker
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null);
        userLocationMarkerRef.current = null;
      }
      
      // Clear route elements
      polylineRef.current.forEach(p => p.setMap(null));
      polylineRef.current = [];
      routeMarkersRef.current.forEach(marker => {
        if (marker.setMap) {
          marker.setMap(null);
        }
      });
      routeMarkersRef.current = [];
      
      // Clear info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [region]);

  // Handle user location
  useEffect(() => {
    if (!isMapReady || !showsUserLocation || !navigator.geolocation || !mapInstanceRef.current) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!mapInstanceRef.current) return;
        
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        
        // Remove existing user location marker
        if (userLocationMarkerRef.current) {
          if ((userLocationMarkerRef.current as any).map !== undefined) {
            // AdvancedMarkerElement
            (userLocationMarkerRef.current as any).map = null;
          } else if ((userLocationMarkerRef.current as any).setMap) {
            // Legacy Marker
            (userLocationMarkerRef.current as any).setMap(null);
          }
        }
        
        // Create new user location marker
        // Use AdvancedMarkerElement if available and Map ID is set, fallback to Marker
        if (USE_ADVANCED_MARKERS && (window as any).google?.maps?.marker?.AdvancedMarkerElement) {
          const circleElement = document.createElement('div');
          circleElement.style.width = '20px';
          circleElement.style.height = '20px';
          circleElement.style.borderRadius = '50%';
          circleElement.style.backgroundColor = '#4285F4';
          circleElement.style.border = '3px solid #FFFFFF';
          circleElement.style.boxSizing = 'border-box';
          
          userLocationMarkerRef.current = new (window as any).google.maps.marker.AdvancedMarkerElement({
            position: userLocation,
            map: mapInstanceRef.current,
            content: circleElement,
            title: 'Your Location',
          });
        } else {
          // Fallback to legacy Marker
          userLocationMarkerRef.current = new (window as any).google.maps.Marker({
            position: userLocation,
            map: mapInstanceRef.current,
            icon: {
              path: (window as any).google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 3,
            },
            title: 'Your Location',
          });
        }
      },
      (error) => console.error('[GPS Map] Error getting user location:', error)
    );
  }, [isMapReady, showsUserLocation]);

  // Update markers when positions change
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current) return;

    // Clear previous markers and info windows
    markerRefs.current.forEach(marker => {
      try {
        if (marker.map !== undefined) {
          // AdvancedMarkerElement
          marker.map = null;
        } else if (marker.setMap) {
          // Legacy Marker
          marker.setMap(null);
        }
      } catch (error) {
        console.error('[GPS Map] Error clearing marker:', error);
      }
    });
    markerRefs.current = [];

    // Clear previous marker info windows
    markerInfoWindowsRef.current.forEach(infoWindow => {
      try {
        infoWindow.close();
      } catch (error) {
        console.error('[GPS Map] Error closing info window:', error);
      }
    });
    markerInfoWindowsRef.current = [];

    // Add GPS device markers
    try {
      positions.forEach(position => {
        if (!mapInstanceRef.current) return;
        
        let marker;
        
        // Use AdvancedMarkerElement if available and Map ID is set, fallback to Marker
        if (USE_ADVANCED_MARKERS && (window as any).google?.maps?.marker?.AdvancedMarkerElement) {
          // Create HTML div with FontAwesome5 car icon (exact same as sidebar)
          const markerColor = position.is_online ? '#007AFF' : '#8E8E93';
          const currentSpeed = position.speed || 0;
          
          // Create marker container
          const markerElement = document.createElement('div');
          markerElement.style.width = '48px';
          markerElement.style.height = '48px';
          markerElement.style.borderRadius = '50%';
          markerElement.style.backgroundColor = markerColor;
          markerElement.style.display = 'flex';
          markerElement.style.alignItems = 'center';
          markerElement.style.justifyContent = 'center';
          markerElement.style.border = '3px solid white';
          markerElement.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
          markerElement.style.cursor = 'pointer';
          markerElement.style.position = 'relative';
          markerElement.title = position.device_label;
          
          // Create inline SVG with exact FontAwesome5 car icon path (same as sidebar)
          const iconSvgString = `
            <svg width="24" height="24" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style="display: block;">
              <path d="M499.99 176h-59.87l-16.64-41.6C406.38 91.63 365.57 64 319.5 64h-127c-46.06 0-86.88 27.63-103.99 70.4L71.87 176H12.01C4.2 176-1.53 183.34.37 190.91l6 24C7.7 220.25 12.5 224 18.01 224h20.07C24.65 235.73 16 252.78 16 272v48c0 16.12 6.16 30.67 16 41.93V416c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-32h256v32c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-54.07c9.84-11.25 16-25.8 16-41.93v-48c0-19.22-8.65-36.27-22.07-48H494c5.51 0 10.31-3.75 11.64-9.09l6-24c1.89-7.57-3.84-14.91-11.65-14.91zm-352.06-17.83c7.29-18.22 24.94-30.17 44.57-30.17h127c19.63 0 37.28 11.95 44.57 30.17L384 208H128l19.93-49.83zM96 319.8c-19.2 0-32-12.76-32-31.9S76.8 256 96 256s48 28.71 48 47.85-28.8 15.95-48 15.95zm320 0c-19.2 0-48 3.19-48-15.95S396.8 256 416 256s32 12.76 32 31.9-12.8 31.9-32 31.9z" fill="white"/>
            </svg>
          `;
          
          // Parse and append SVG
          const parser = new DOMParser();
          const iconSvg = parser.parseFromString(iconSvgString.trim(), 'image/svg+xml').documentElement;
          markerElement.appendChild(iconSvg);
          
          // Add speed badge if moving
          if (currentSpeed > 5) {
            const speedBadge = document.createElement('div');
            speedBadge.style.position = 'absolute';
            speedBadge.style.top = '-4px';
            speedBadge.style.right = '-4px';
            speedBadge.style.width = '20px';
            speedBadge.style.height = '20px';
            speedBadge.style.borderRadius = '50%';
            speedBadge.style.backgroundColor = '#FF9500';
            speedBadge.style.border = '2px solid white';
            speedBadge.style.display = 'flex';
            speedBadge.style.alignItems = 'center';
            speedBadge.style.justifyContent = 'center';
            speedBadge.style.fontSize = '9px';
            speedBadge.style.fontWeight = 'bold';
            speedBadge.style.color = 'white';
            speedBadge.style.lineHeight = '1';
            speedBadge.textContent = Math.round(currentSpeed).toString();
            markerElement.appendChild(speedBadge);
          }
          
          marker = new (window as any).google.maps.marker.AdvancedMarkerElement({
            position: { lat: position.latitude, lng: position.longitude },
            map: mapInstanceRef.current,
            content: markerElement,
            title: position.device_label,
          });
        } else {
          // Fallback to legacy Marker - uses SVG with FontAwesome5 car path
          marker = new (window as any).google.maps.Marker({
            position: { lat: position.latitude, lng: position.longitude },
            map: mapInstanceRef.current,
            icon: {
              url: createGPSMarkerIcon(position.is_online, position.speed || 0),
              scaledSize: new (window as any).google.maps.Size(48, 48),
              anchor: new (window as any).google.maps.Point(24, 24),
            },
            title: position.device_label,
          });
        }

        // Create info window content
        const createInfoContent = (pos: LivePosition) => {
          const vehicleInfo = pos.vehicle ? 
            `${(pos.vehicle as any).registrationNumber || (pos.vehicle as any).registration_number || 'No Plate'} - ${[pos.vehicle.make, pos.vehicle.model].filter(Boolean).join(' ')}` : 
            'No Vehicle';
          
          const driverInfo = pos.driver ? pos.driver.name : 'No Driver';
          const speed = (pos.speed || 0).toFixed(0);
          const time = pos.timestamp ? new Date(pos.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'No Data';
          
          return `
            <div style="
              padding: 12px; 
              min-width: 220px; 
              max-width: 280px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: white;
              border-radius: 6px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
            ">
              <!-- Compact Header -->
              <div style="
                display: flex; 
                align-items: center; 
                gap: 6px; 
                margin-bottom: 8px; 
                padding-bottom: 6px; 
                border-bottom: 1px solid #E5E5EA;
              ">
                <div style="
                  width: 8px; 
                  height: 8px; 
                  border-radius: 4px; 
                  background: ${pos.is_online ? '#007AFF' : '#8E8E93'};
                "></div>
                <strong style="font-size: 14px; color: #1A1A1A; font-weight: 700;">${pos.device_label}</strong>
              </div>
              
              <!-- Compact Vehicle Info -->
              <div style="margin-bottom: 8px;">
                <div style="
                  display: flex; 
                  align-items: center; 
                  gap: 6px; 
                  padding: 6px; 
                  background: #F8F9FA; 
                  border-radius: 4px;
                ">
                  <span style="font-size: 12px;">🚗</span>
                  <div>
                    <div style="
                      font-size: 13px; 
                      font-weight: 700; 
                      color: #007AFF; 
                      letter-spacing: 0.3px;
                      text-transform: uppercase;
                    ">${(pos.vehicle as any)?.registrationNumber || (pos.vehicle as any)?.registration_number || 'NO PLATE'}</div>
                    <div style="font-size: 11px; color: #6B7280;">
                      ${[pos.vehicle?.make, pos.vehicle?.model].filter(Boolean).join(' ')}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Compact Driver & Stats -->
              <div style="
                display: flex; 
                align-items: center; 
                gap: 8px; 
                margin-bottom: 8px;
              ">
                ${pos.driver ? `
                  <div style="
                    display: flex; 
                    align-items: center; 
                    gap: 4px; 
                    padding: 4px 6px; 
                    background: #F0FDF4; 
                    border-radius: 4px;
                  ">
                    <span style="font-size: 10px;">👤</span>
                    <span style="font-size: 11px; color: #059669; font-weight: 600;">${pos.driver.name}</span>
                  </div>
                ` : ''}
                
                <div style="
                  display: flex; 
                  align-items: center; 
                  gap: 4px; 
                  padding: 4px 6px; 
                  background: #FEF3F2; 
                  border-radius: 4px;
                ">
                  <span style="font-size: 10px;">⚡</span>
                  <span style="font-size: 11px; color: #DC2626; font-weight: 600;">${speed}km/h</span>
                </div>
                
                <div style="
                  display: flex; 
                  align-items: center; 
                  gap: 4px; 
                  padding: 4px 6px; 
                  background: #F3F4F6; 
                  border-radius: 4px;
                ">
                  <span style="font-size: 10px;">🕐</span>
                  <span style="font-size: 11px; color: #374151; font-weight: 600;">${time}</span>
                </div>
              </div>
              
              <!-- Compact Coordinates -->
              <div style="
                padding: 4px 6px; 
                background: #F9FAFB; 
                border-radius: 3px; 
                font-size: 9px; 
                color: #9CA3AF; 
                font-family: monospace;
                text-align: center;
              ">
                📍 ${pos.latitude.toFixed(4)}, ${pos.longitude.toFixed(4)}
              </div>
            </div>
          `;
        };

        // Create a separate info window for hover
        const markerInfoWindow = new (window as any).google.maps.InfoWindow({
          content: createInfoContent(position),
          disableAutoPan: true,
          maxWidth: 280,
        });

        let isHovering = false;
        let isClicked = false;

        // Add hover listener for quick preview
        marker.addListener('mouseover', () => {
          if (!isClicked && mapInstanceRef.current) {
            isHovering = true;
            markerInfoWindow.open(mapInstanceRef.current, marker);
          }
        });

        // Add mouseout listener to close on hover out
        marker.addListener('mouseout', () => {
          isHovering = false;
          // Small delay to prevent flickering when moving between marker and info window
          setTimeout(() => {
            if (!isClicked) {
              markerInfoWindow.close();
            }
          }, 150);
        });

        // Add click listener for permanent info window
        marker.addListener('click', (e: any) => {
          if (e.stop) e.stop();
          isClicked = true;
          
          // Close hover window
          markerInfoWindow.close();
          
          // Close any existing permanent info window
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
          
          // Create and open permanent info window
          if (mapInstanceRef.current) {
            infoWindowRef.current = new (window as any).google.maps.InfoWindow({
              content: createInfoContent(position),
              disableAutoPan: false,
              maxWidth: 280,
            });
            
            infoWindowRef.current.open(mapInstanceRef.current, marker);
            
            // Add listener to reset clicked state when info window closes
            infoWindowRef.current.addListener('closeclick', () => {
              isClicked = false;
            });
          }
          
          if (onMarkerPress) {
            onMarkerPress(position);
          }
        });

        markerRefs.current.push(marker);
        markerInfoWindowsRef.current.push(markerInfoWindow);
      });

      // Fit map to show all markers (only valid positions)
      if (positions.length > 0 && mapInstanceRef.current) {
        const validPositions = positions.filter(p => 
          p.latitude && p.longitude && 
          (p.latitude !== 0 || p.longitude !== 0) &&
          !isNaN(p.latitude) && !isNaN(p.longitude)
        );
        
        if (validPositions.length > 0) {
          try {
            const bounds = new (window as any).google.maps.LatLngBounds();
            validPositions.forEach(p => {
              bounds.extend(new (window as any).google.maps.LatLng(p.latitude, p.longitude));
            });
            
            // Only fit bounds if bounds are valid
            if (!bounds.isEmpty()) {
              mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
            }
          } catch (error) {
            console.error('[GPS Map] Error fitting bounds:', error);
          }
        }
      }
    } catch (error) {
      console.error('[GPS Map] Error updating markers:', error);
    }
  }, [isMapReady, positions, onMarkerPress]);

  // Highlight selected device
  useEffect(() => {
    if (!isMapReady || !selectedDeviceId || !mapInstanceRef.current) return;
    
    const selectedPosition = positions.find(p => p.device_id === selectedDeviceId);
    if (selectedPosition && selectedPosition.latitude && selectedPosition.longitude) {
      try {
        mapInstanceRef.current.setCenter({
          lat: selectedPosition.latitude,
          lng: selectedPosition.longitude,
        });
        mapInstanceRef.current.setZoom(16);
      } catch (error) {
        console.error('[GPS Map] Error centering on selected device:', error);
      }
    }
  }, [isMapReady, selectedDeviceId, positions]);

  // Render route polyline
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current || !showRoute) return;

    // Clear previous route elements
    polylineRef.current.forEach(p => p.setMap(null));
    polylineRef.current = [];
    routeMarkersRef.current.forEach(marker => {
      if (marker.map !== undefined) {
        marker.map = null;
      } else if (marker.setMap) {
        marker.setMap(null);
      }
    });
    routeMarkersRef.current = [];

    if (routePositions.length === 0) {
      setRouteMaxSpeed(0);
      return;
    }

    // Speed-to-color: green (low) → yellow (mid) → red (high)
    const getSpeedColor = (speedKmh: number, maxSpeedKmh: number): string => {
      const ratio = Math.min(Math.max(speedKmh / Math.max(maxSpeedKmh, 1), 0), 1);
      // HSL hue: 120 (green) → 60 (yellow) → 0 (red)
      const hue = 120 * (1 - ratio);
      return `hsl(${hue}, 100%, 42%)`;
    };

    try {
      // Calculate max speed for color scaling (knots → km/h)
      const speedsKmh = routePositions.map(pos => (pos.speed || 0) * 1.852);
      const maxSpeed = Math.max(...speedsKmh, 1);
      setRouteMaxSpeed(maxSpeed);

      // Create color-coded polyline segments based on speed
      for (let i = 0; i < routePositions.length - 1; i++) {
        const curr = routePositions[i];
        const next = routePositions[i + 1];
        const avgSpeed = ((curr.speed || 0) + (next.speed || 0)) / 2 * 1.852;
        const color = getSpeedColor(avgSpeed, maxSpeed);

        const segment = new (window as any).google.maps.Polyline({
          path: [
            { lat: curr.latitude, lng: curr.longitude },
            { lat: next.latitude, lng: next.longitude },
          ],
          geodesic: true,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 5,
          map: mapInstanceRef.current,
        });

        // Click on segment shows speed popup
        const segmentTime = curr.fixTime ? new Date(curr.fixTime).toLocaleTimeString('pt-PT') : '';
        const segmentInfoWindow = new (window as any).google.maps.InfoWindow();
        segment.addListener('click', (e: any) => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
          segmentInfoWindow.setContent(`
            <div style="padding: 6px 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="width: 8px; height: 8px; border-radius: 50%; background: ${color}; display: inline-block;"></span>
                <strong style="font-size: 14px; color: #1a1a1a;">${avgSpeed.toFixed(0)} km/h</strong>
              </div>
              ${segmentTime ? `<div style="font-size: 11px; color: #888; margin-top: 2px;">🕐 ${segmentTime}</div>` : ''}
            </div>
          `);
          segmentInfoWindow.setPosition(e.latLng);
          segmentInfoWindow.open(mapInstanceRef.current);
          infoWindowRef.current = segmentInfoWindow;
        });

        polylineRef.current.push(segment);
      }

      // Build path for bounds fitting
      const path = routePositions.map(pos => ({
        lat: pos.latitude,
        lng: pos.longitude,
      }));

      // Add start marker (green)
      const startPos = routePositions[0];
      const startMarker = new (window as any).google.maps.Marker({
        position: { lat: startPos.latitude, lng: startPos.longitude },
        map: mapInstanceRef.current,
        icon: {
          path: (window as any).google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#34C759',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        title: `Início - ${startPos.fixTime ? new Date(startPos.fixTime).toLocaleString() : ''}`,
        zIndex: 1000,
      });

      // Add start info window
      const startInfoWindow = new (window as any).google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <strong style="color: #34C759;">🟢 Início</strong><br/>
            <small>${startPos.fixTime ? new Date(startPos.fixTime).toLocaleString('pt-PT') : 'N/A'}</small><br/>
            <small>${startPos.speed !== undefined ? `${startPos.speed.toFixed(0)} km/h` : ''}</small>
          </div>
        `,
      });

      startMarker.addListener('click', () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        startInfoWindow.open(mapInstanceRef.current, startMarker);
      });

      routeMarkersRef.current.push(startMarker);

      // Add end marker (red) if there are multiple positions
      if (routePositions.length > 1) {
        const endPos = routePositions[routePositions.length - 1];
        const endMarker = new (window as any).google.maps.Marker({
          position: { lat: endPos.latitude, lng: endPos.longitude },
          map: mapInstanceRef.current,
          icon: {
            path: (window as any).google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#FF3B30',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
          },
          title: `Fim - ${endPos.fixTime ? new Date(endPos.fixTime).toLocaleString() : ''}`,
          zIndex: 1000,
        });

        // Add end info window
        const endInfoWindow = new (window as any).google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <strong style="color: #FF3B30;">🔴 Fim</strong><br/>
              <small>${endPos.fixTime ? new Date(endPos.fixTime).toLocaleString('pt-PT') : 'N/A'}</small><br/>
              <small>${endPos.speed !== undefined ? `${endPos.speed.toFixed(0)} km/h` : ''}</small>
            </div>
          `,
        });

        endMarker.addListener('click', () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
          endInfoWindow.open(mapInstanceRef.current, endMarker);
        });

        routeMarkersRef.current.push(endMarker);
      }

      // Fit map to route bounds
      if (path.length > 0) {
        const bounds = new (window as any).google.maps.LatLngBounds();
        path.forEach((point: any) => {
          bounds.extend(point);
        });
        
        if (!bounds.isEmpty()) {
          mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
        }
      }
    } catch (error) {
      console.error('[GPS Map] Error rendering route:', error);
    }
  }, [isMapReady, showRoute, routePositions]);

  useEffect(() => {
    if (!isMapReady || !showRoute) return;

    alertMarkersRef.current.forEach((marker) => {
      if (marker.map !== undefined) {
        marker.map = null;
      } else if (marker.setMap) {
        marker.setMap(null);
      }
    });
    alertMarkersRef.current = [];

    if (!alertMarkers.length) return;

    try {
      alertMarkers.forEach((alert) => {
        const marker = new (window as any).google.maps.Marker({
          position: { lat: alert.latitude, lng: alert.longitude },
          map: mapInstanceRef.current,
          icon: {
            path: (window as any).google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: alert.color || '#FF9500',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
          title: alert.title || 'Alerta',
          zIndex: 900,
        });

        // Build rich popup content for event markers
        const popupParts: string[] = [];
        popupParts.push(`<div style="padding: 10px 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; min-width: 200px;">`);
        
        // Title with colored indicator
        popupParts.push(`<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">`);
        popupParts.push(`<span style="width: 10px; height: 10px; border-radius: 50%; background: ${alert.color || '#FF9500'}; display: inline-block; flex-shrink: 0;"></span>`);
        popupParts.push(`<strong style="font-size: 14px; color: #1a1a1a;">${alert.title || 'Alerta'}</strong>`);
        popupParts.push(`</div>`);

        // Details section
        popupParts.push(`<div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #555;">`);
        
        // Date/time
        if (alert.description) {
          popupParts.push(`<div style="display: flex; align-items: center; gap: 6px;"><span style="color: #888;">🕐</span> ${alert.description}</div>`);
        }

        // Device name
        if (alert.deviceName) {
          popupParts.push(`<div style="display: flex; align-items: center; gap: 6px;"><span style="color: #888;">🚗</span> ${alert.deviceName}</div>`);
        }

        // Speed at the time of event
        if (alert.speed !== undefined && alert.speed !== null) {
          const speedStr = `${alert.speed.toFixed(0)} km/h`;
          popupParts.push(`<div style="display: flex; align-items: center; gap: 6px;"><span style="color: #888;">⚡</span> Velocidade: <strong>${speedStr}</strong></div>`);
        }

        // Speed limit (for overspeed events)
        if (alert.speedLimit !== undefined && alert.speedLimit !== null) {
          popupParts.push(`<div style="display: flex; align-items: center; gap: 6px;"><span style="color: #888;">🚫</span> Limite: <strong>${alert.speedLimit.toFixed(0)} km/h</strong></div>`);
        }

        // Show overspeed delta
        if (alert.speed && alert.speedLimit && alert.speed > alert.speedLimit) {
          const delta = (alert.speed - alert.speedLimit).toFixed(0);
          popupParts.push(`<div style="display: flex; align-items: center; gap: 6px; color: #FF3B30; font-weight: 600;"><span>⚠️</span> Excesso: +${delta} km/h</div>`);
        }

        // Address
        if (alert.address) {
          popupParts.push(`<div style="display: flex; align-items: center; gap: 6px;"><span style="color: #888;">📍</span> ${alert.address}</div>`);
        }

        // Coordinates
        popupParts.push(`<div style="display: flex; align-items: center; gap: 6px; color: #aaa; font-size: 10px;"><span>📌</span> ${alert.latitude.toFixed(5)}, ${alert.longitude.toFixed(5)}</div>`);

        popupParts.push(`</div></div>`);

        const infoWindow = new (window as any).google.maps.InfoWindow({
          content: popupParts.join(''),
        });
        marker.addListener('click', () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
          infoWindow.open(mapInstanceRef.current, marker);
          infoWindowRef.current = infoWindow;
        });

        alertMarkersRef.current.push(marker);
      });
    } catch (error) {
      console.error('[GPS Map] Error rendering alert markers:', error);
    }
  }, [isMapReady, showRoute, alertMarkers]);

  return (
    <View style={[styles.container, style]}>
      {!isMapReady && !mapError && (
        <LoadingScreen message="Loading map..." fullScreen={false} />
      )}
      {mapError && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{mapError}</Text>
          <Text style={styles.errorHint}>
            Check browser console for details or try refreshing the page.
          </Text>
        </View>
      )}
      <div 
        ref={mapRef} 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#E5E5EA',
        }} 
      />
      {/* Speed color legend for routes */}
      {showRoute && routePositions.length > 1 && routeMaxSpeed > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 24,
          left: 12,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          padding: '8px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: 11,
          zIndex: 100,
          pointerEvents: 'none',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#333', fontSize: 11 }}>
            Velocidade
          </div>
          <div style={{
            width: 140,
            height: 10,
            borderRadius: 5,
            background: 'linear-gradient(to right, hsl(120, 100%, 42%), hsl(60, 100%, 42%), hsl(0, 100%, 42%))',
          }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, color: '#666' }}>
            <span>0</span>
            <span>{Math.round(routeMaxSpeed / 2)}</span>
            <span>{Math.round(routeMaxSpeed)} km/h</span>
          </div>
        </div>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    flex: 1,
    width: '100%',
    minHeight: 300,
    backgroundColor: '#E5E5EA',
  },
  map: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#E5E5EA',
    zIndex: 1,
  },
  loadingText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 8,
  },
  errorContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFEBEE',
    zIndex: 2,
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    color: '#FF3B30',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  errorHint: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'center',
  },
});

export default GPSMapView;

